import { type Component, type JSX, useEffect, useRef, useState } from 'react';
import { type DisclosureOptions } from 'reakit';

import { categoryGroups } from '@cx-modules/constants';
import { useTranslation } from '@cx-modules/i18n-provider';
import { Accordion, Arrow, type TabNavItem, TabNavWithScrollButtons } from '@cx-modules/ui';
import { mergePersistentParams } from '@cx-modules/utils';

import { type LoadOutrightsCouponArgs } from '../../types';
import { type OutrightMarketDto } from '../_common';

import { CompactBetsGrid, DefaultBetsGrid } from './components';
import { useMarketTranslator } from './MarketTranslator';
import {
  AllOutrightsLink,
  AllOutrightsLinkWrapper,
  arrow,
  ArrowWrapper,
  CompareOddsLink,
  CompareOddsLinkContainer,
  innerWrapperClass,
  leftBtnClass,
  MarketListWrapper,
  MarketTableWrapper,
  MarketTitleLink,
  MarketTitleLinkContainer,
  OddsTableHeadersContainer,
  OddsTableWrapper,
  outer,
  OutrightMarketsTitle,
  OutrightMarketsWrapper,
  rightBtnClass,
  TabNavContainer,
  tabNavStyle,
} from './style';

export interface OutrightMarketsProps {
  compactView?: boolean;
  headerTitle?: string;
  newUI?: boolean;
  oddsShown: LoadOutrightsCouponArgs['oddsShown'];
  outrightMarkets: OutrightMarketDto[];
  requestUrl: string;
  showAllOutrightsLink?: boolean;
  showFilters?: boolean;
  visibleMarkets?: number;
}

export const OutrightMarkets = ({
  compactView,
  headerTitle,
  newUI,
  oddsShown,
  outrightMarkets = [],
  requestUrl,
  showAllOutrightsLink,
  showFilters,
}: OutrightMarketsProps): JSX.Element => {
  const categoryGroup = categoryGroups.find(cg => outrightMarkets.some(market => market.categoryGroupId === cg.id));
  const categoryGroupTranslationNameSpace = categoryGroup?.translationFile;
  const sportOutrightNs = categoryGroupTranslationNameSpace
    ? `bettype_${categoryGroupTranslationNameSpace}_outright`
    : null;
  const nsOrder = [
    ...(sportOutrightNs ? [sportOutrightNs] : []),
    ...(categoryGroupTranslationNameSpace ? [categoryGroupTranslationNameSpace] : []),
    'bettype',
    'sports',
    'label',
  ];

  const { t } = useTranslation(nsOrder as any);
  const translateMarket = useMarketTranslator(categoryGroupTranslationNameSpace);

  const marketTabs = outrightMarkets.map(({ marketId, marketName, subEventName }) => ({
    dataTrack: `outrights-market-filters-${marketName.replace(/\s+/g, '-')}`,
    id: marketId.toString(),
    name: marketName,
    subEventName,
  }));

  const accordionRef = useRef<Component<'button', DisclosureOptions, any>>(null);
  const [activeTab, setActiveTab] = useState<TabNavItem>(marketTabs[0]);
  const allOutrightsLinkHandler = () => window.scrollTo(0, 0);
  const visibleMarketsList = showFilters
    ? outrightMarkets.filter(({ marketId }) => marketId.toString() === activeTab.id)
    : outrightMarkets;
  const competitionName = outrightMarkets[0]?.subEventName;

  const outrightsMarketFilterRef = useRef<HTMLDivElement | null>(null);
  const [scrollIncrement, setScrollIncrement] = useState(0);
  function getContainerWidth(ref: React.RefObject<HTMLDivElement | null>): number {
    return ref?.current?.offsetWidth || 0;
  }
  useEffect(() => setScrollIncrement(getContainerWidth(outrightsMarketFilterRef) * 0.8), []);

  const renderOutrightMarkets = () => {
    if (!outrightMarkets.length) {
      return 'No outrights available at the moment. Check again later.';
    }

    return (
      <MarketListWrapper className={newUI ? 'newUI' : ''}>
        {visibleMarketsList.map(({ bets, marketId, marketName, subEventName, url }, i) => {
          const sortedBets = (bets || []).slice().sort((a, b) => a.bestOddsDecimal - b.bestOddsDecimal);

          const outrightsOddsTableHeader = () => (
            <OddsTableHeadersContainer className={newUI ? `newUI ${arrow}` : ''} data-testid="outright-market">
              <MarketTitleLinkContainer>
                <MarketTitleLink
                  className={url ? '' : 'disabled'}
                  data-testid="market-title"
                  href={url ? mergePersistentParams(url, requestUrl) : undefined}
                >
                  {t(subEventName, { ns: categoryGroupTranslationNameSpace })} | {translateMarket(marketName)}
                </MarketTitleLink>
              </MarketTitleLinkContainer>
              {newUI && url ? null : (
                <CompareOddsLinkContainer>
                  <CompareOddsLink
                    data-track="outrights-compare-odds-link"
                    href={url ? mergePersistentParams(url, requestUrl) : undefined}
                  >
                    Compare odds<span>(25+ Bookmakers)</span>
                  </CompareOddsLink>
                </CompareOddsLinkContainer>
              )}
            </OddsTableHeadersContainer>
          );

          const outrightsOddsTableBody = () => (
            <>
              <OddsTableWrapper
                className={newUI ? 'newUI' : ''}
                data-testid={`${subEventName} | ${marketName}-markets`}
              >
                {compactView ? (
                  <CompactBetsGrid
                    bets={sortedBets}
                    categoryGroup={categoryGroup}
                    marketId={marketId}
                    marketName={marketName}
                    marketUrl={url}
                    newUI={newUI}
                    oddsShown={oddsShown}
                    requestUrl={requestUrl}
                    subEventName={subEventName}
                  />
                ) : (
                  <DefaultBetsGrid
                    bets={sortedBets}
                    categoryGroup={categoryGroup}
                    marketName={marketName}
                    marketUrl={url}
                    oddsShown={oddsShown}
                    requestUrl={requestUrl}
                    subEventName={subEventName}
                  />
                )}
              </OddsTableWrapper>
              {newUI && url ? (
                <CompareOddsLinkContainer className="newUI">
                  <CompareOddsLink
                    className="newUI"
                    data-testid="more-odds"
                    data-track="outrights-compare-odds-link"
                    href={mergePersistentParams(url, requestUrl)}
                  >
                    {t('CX_MORE_ODDS', { ns: 'label' })}{' '}
                    <ArrowWrapper>
                      <Arrow borderWidth={1} colour="var(--ogm-timberwolves)" right size={3} x={0} y={-2} />
                    </ArrowWrapper>
                  </CompareOddsLink>
                </CompareOddsLinkContainer>
              ) : null}
            </>
          );

          if (newUI) {
            return (
              <Accordion
                header={outrightsOddsTableHeader()}
                id={`accordion-${marketId}`}
                key={`${marketId}-${marketName}`}
                ref={accordionRef}
                visible={i < 2}
              >
                {outrightsOddsTableBody()}
              </Accordion>
            );
          }

          return (
            <MarketTableWrapper className={newUI ? 'newUI' : ''} key={`${marketId}-${marketName}`}>
              {outrightsOddsTableHeader()}
              {outrightsOddsTableBody()}
            </MarketTableWrapper>
          );
        })}
      </MarketListWrapper>
    );
  };

  return (
    <OutrightMarketsWrapper data-testid="outrights-coupon">
      {!!headerTitle && (
        <OutrightMarketsTitle data-testid="outrights-title" /* onClick={toggleNewUI} */>
          {headerTitle}
        </OutrightMarketsTitle>
      )}
      {showFilters && (
        <TabNavContainer ref={outrightsMarketFilterRef}>
          <TabNavWithScrollButtons
            activeItemId={activeTab.id}
            ariaLabel={`${headerTitle} Market Filters`}
            className={tabNavStyle}
            id={`${headerTitle}-market-filters`}
            innerWrapperClass={innerWrapperClass}
            items={marketTabs}
            leftBtnClass={leftBtnClass}
            onClick={(item: TabNavItem) => setActiveTab(item)}
            outerWrapperClass={outer}
            rightBtnClass={rightBtnClass}
            scrollIncrement={scrollIncrement}
            type="pill"
          />
        </TabNavContainer>
      )}
      {renderOutrightMarkets()}
      {showAllOutrightsLink && (
        <AllOutrightsLinkWrapper>
          <AllOutrightsLink data-track="all-outrights-link" onClick={allOutrightsLinkHandler}>
            {competitionName} {headerTitle}
          </AllOutrightsLink>
        </AllOutrightsLinkWrapper>
      )}
    </OutrightMarketsWrapper>
  );
};
